import { Arg, Args, Ctx, FieldResolver, Query, Resolver, Root } from 'type-graphql'

import { User } from '@things-factory/auth-base'
import { Domain, getQueryBuilderFromListParams, getRepository, ListParam } from '@things-factory/shell'

import { Board } from '../board/board.js'
import { Group } from './group.js'
import { GroupList } from './group-type.js'

@Resolver(Group)
export class GroupQuery {
  @Query(returns => Group, { nullable: true, description: 'Finds a single board group by its ID.' })
  async group(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<Group> {
    const { domain } = context.state

    return await getRepository(Group).findOne({
      where: { domain: { id: domain.id }, id }
    })
  }

  @Query(returns => GroupList, { description: 'Retrieves a paginated list of board groups.' })
  async groups(@Args(type => ListParam) params: ListParam, @Ctx() context: ResolverContext): Promise<GroupList> {
    const { domain } = context.state

    const queryBuilder = getQueryBuilderFromListParams({
      repository: getRepository(Group),
      params,
      domain,
      searchables: ['name', 'description']
    })

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @FieldResolver(type => [Board], { description: 'Resolves the list of boards belonging to the group.' })
  async boards(@Root() group: Group) {
    return await getRepository(Board).findBy({
      group: { id: group.id }
    })
  }

  @FieldResolver(type => Domain, { description: 'Resolves the domain associated with the group.' })
  async domain(@Root() group: Group) {
    return await getRepository(Domain).findOneBy({ id: group.domainId })
  }

  @FieldResolver(type => User, { description: 'Resolves the user who last updated the group.' })
  async updater(@Root() group: Group): Promise<User> {
    return await getRepository(User).findOneBy({ id: group.updaterId })
  }

  @FieldResolver(type => User, { description: 'Resolves the user who created the group.' })
  async creator(@Root() group: Group): Promise<User> {
    return await getRepository(User).findOneBy({ id: group.creatorId })
  }
}
